import React from 'react';

import moment from 'moment';
import { useTheme } from 'styled-components';
import { ThemeType } from 'theme/theme';

import { getTurnDays } from 'components/Cards/DataCardGroupedFlights/utils';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Sector } from 'models/sector';
import { getAirlineForInitials } from 'utils/generic';
import { ReactComponent as BackpackActive } from 'assets/images/luggages/backpack-active.svg';
import { ReactComponent as HandLuggageActive } from 'assets/images/luggages/hand-luggage-active.svg';
import { ReactComponent as HandLuggage } from 'assets/images/luggages/hand-luggage.svg';
import { ReactComponent as WarehouseLuggageActive } from 'assets/images/luggages/warehouse-luggage-active.svg';
import { ReactComponent as WarehouseLuggage } from 'assets/images/luggages/warehouse-luggage.svg';

import {
  AirlineLogo,
  AirlineName,
  Airport,
  Arrival,
  Arrow,
  Departure,
  DepartureArrivalTime,
  Flight,
  FlightLuggages,
  RightText,
  StopsLine,
  TapArea,
  TurnDays,
} from './styles';

type Props = {
  sector: Sector;
  hideArrow?: boolean;
};
const SectorFlight = ({ sector, hideArrow = false }: Props) => {
  const { t } = useStoreTranslation();
  const firstFlight = sector.flights.first();
  const lastFlight = sector.flights.last();
  const theme = useTheme() as ThemeType;

  const turnDays = getTurnDays(moment(firstFlight.departureDate).toDate(), moment(lastFlight.arrivalDate).toDate());

  const extractTime = (date: Date) => moment(date).utc().format('HH:mm');

  return (
    <Flight key={sector.sectorNumber}>
      <AirlineLogo airline={firstFlight.airlineCode} />
      <Departure>
        <DepartureArrivalTime>{extractTime(moment(firstFlight.departureDate).toDate())}</DepartureArrivalTime>
        <Airport>{firstFlight.departureAirport}</Airport>
      </Departure>
      <StopsLine
        topText={moment.duration(sector.totalDurationInSeconds, 'seconds').format(
          function () {
            if ((this as any).duration.asSeconds() >= 24 * 60 * 60) return 'd[d] h[h]';
            return 'h[h] m[m]';
          },
          { trim: 'all' },
        )}
        bottomText={
          sector.flights.length === 1
            ? t('flightsCard.direct')
            : t('flightsCard.stopNumber', { count: sector.flights.length - 1 })
        }
        points={sector.flights.length - 1}
      />
      <Arrival>
        {turnDays > 0 && <TurnDays>{`+${turnDays}`}</TurnDays>}
        <DepartureArrivalTime>{extractTime(moment(lastFlight.arrivalDate).toDate())}</DepartureArrivalTime>
        <Airport>{lastFlight.arrivalAirport}</Airport>
      </Arrival>
      <TapArea>
        <RightText>
          <AirlineName>{getAirlineForInitials(firstFlight.airlineCode)?.split(' ').first()}</AirlineName>
          <FlightLuggages>
              <BackpackActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              {firstFlight.handBaggage ? (
                <HandLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              ) : (
                <HandLuggage />
              )}
              {firstFlight.baggageRestrictions === undefined || (firstFlight.baggageRestrictions !== undefined && firstFlight.baggageRestrictions.startsWith('0')) ? (
                <WarehouseLuggage />
              ) : (
                <WarehouseLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              )}
            </FlightLuggages>
        </RightText>
        {!hideArrow && <Arrow />}
      </TapArea>
    </Flight>
  );
};
export default SectorFlight;
